import React, { useState, useRef, useEffect } from "react";
import CheckBoxWithLabel from "../checkbox/CheckBoxWithLabel";
import CustomRadioButton from "../buttons/CustomRadioButton";
import CustomDatePicker from "../DatePicker/CustomDatePicker";
import Button from "../buttons/Button";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import useTranslation from "../CustomHooks/useTranslation";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import dayjs from "dayjs";
import ButtonWithImg from "../buttons/ButtonWithImg";

// Filter option type
export type DynamicFilterConfig = {
  [section: string]: {
    label: string;
    type: "checkbox" | "radio" | "date";
    options?: string[]; // for checkbox/radio
    default?: string | string[];
    required?: boolean;
    searchKey: string | { start: string; end: string }; // updated for date
    valueMapping?: { [displayValue: string]: string }; // new: maps display values to search param values
    testId?: string
  };
};

interface DynamicFilterPanelProps {
  config: DynamicFilterConfig;
  onApply: (filters: { [key: string]: any }) => void;
  onClear?: () => void;
  initialValues?: { [key: string]: any };
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  customHeight?: string;
}

const DynamicFilterPanel: React.FC<DynamicFilterPanelProps> = ({
  config,
  onApply,
  onClear,
  initialValues = {},
  loading = false,
  disabled = false,
  className = "",
  customHeight,
}) => {
  const { translate, notEnglish } = useTranslation(breadcrumbsContent);
  const [activeSection, setActiveSection] = useState<string>(Object.keys(config)[0] || "");
  const [values, setValues] = useState<{ [key: string]: any }>(initialValues);
  const [errorMessage, setErrorMessage] = useState<string | { start?: string; end?: string }>('');
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setErrorMessage('');
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Initialize values from initialValues prop
  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      setValues(initialValues);
    }
  }, [initialValues]);

  // Clear error message when active filter changes
  useEffect(() => {
    setErrorMessage('');
  }, [activeSection]);

  // Handlers for each type
  const handleCheckbox = (section: string, option: string) => {
    setValues((prev) => {
      const arr = Array.isArray(prev[section]) ? [...prev[section]] : [];
      if (arr.includes(option)) {
        return { ...prev, [section]: arr.filter((v) => v !== option) };
      } else {
        return { ...prev, [section]: [...arr, option] };
      }
    });
  };
  const handleRadio = (section: string, value: string) => {
    setValues((prev) => ({ ...prev, [section]: value }));
  };
  const handleDateChange = (section: string, date: dayjs.Dayjs | null, type: "start" | "end") => {
    // debugger
    setValues((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [type]: date,
      },
    }));

    // Date validation
    const currentValues = values[section] || {};
    const newValues = { ...currentValues, [type]: date };
    
    if (newValues.start && newValues.end && newValues.start.isAfter(newValues.end)) {
      setErrorMessage(`${translate("Start date cannot be greater than end date")}`);
    } else {
      setErrorMessage('');
    }
  };

  // Validation and apply
  const handleApply = () => {
    // If the active section is a date filter, apply specific validation logic
    const activeConf = config[activeSection];
    if (activeConf && activeConf.type === "date") {
      const dateValue = values[activeSection] || {};
      const searchKey = activeConf.searchKey;
      // If searchKey is a string, only require one date (start or end)
      if (typeof searchKey === "string") {
        if (!dateValue.date) {
          setErrorMessage(translate("Please select a date"));
          return;
        }
      } else {
        // If searchKey is an object, require both start and end if required
        const startDate = dateValue.start;
        const endDate = dateValue.end;
        let errors: { start?: string; end?: string } = {};
        if (!startDate && !endDate) {
          errors.start = translate("This field is required");
          setErrorMessage(errors);
          return;
        }
        if (startDate && !endDate) {
          errors.end = translate("Please select end date");
          setErrorMessage(errors);
          return;
        }
        if (!startDate && endDate) {
          errors.start = translate("Please select start date");
          setErrorMessage(errors);
          return;
        }
        if (startDate && endDate && startDate.isAfter(endDate)) {
          setErrorMessage(`${translate("Start date cannot be greater than end date")}`);
          return;
        }
      }
    }

    // Clear start date if only start is set and end is not set for any date filter (only for object searchKey)
    const cleanedValues = { ...values };
    console.log(cleanedValues, 'values')
    Object.entries(config).forEach(([key, conf]) => {
      if (conf.type === "date" && typeof conf.searchKey === "object" && cleanedValues[key]) {
        const dateValue = cleanedValues[key];
        if ((dateValue.start && !dateValue.end) || (!dateValue.start && dateValue.end)) {
          cleanedValues[key] = { start: undefined, end: undefined };
        }
      }
    });

    // Example: check required fields
    for (const key in config) {
      const conf = config[key];
      if (conf.required) {
        if (conf.type === "date") {
          if (typeof conf.searchKey === "object") {
            if (!cleanedValues[key]?.start || !cleanedValues[key]?.end) {
              setErrorMessage(`${conf.label}: ${translate("Both dates required")}`);
              return;
            }
            // Date validation: start date cannot be after end date
            if (cleanedValues[key].start && cleanedValues[key].end && cleanedValues[key].start.isAfter(cleanedValues[key].end)) {
              setErrorMessage(`${conf.label}: ${translate("Start date cannot be greater than end date")}`);
              return;
            }
          } else if (typeof conf.searchKey === "string") {
            // Only require one date (start or end)
            if (!cleanedValues[key]?.start && !cleanedValues[key]?.end) {
              setErrorMessage(`${conf.label}: ${translate("This field is required")}`);
              return;
            }
          }
        } else if (!cleanedValues[key] || (Array.isArray(cleanedValues[key]) && cleanedValues[key].length === 0)) {
          setErrorMessage(`${conf.label}: ${translate("This field is required")}`);
          return;
        }
      }
    }

    // Additional date validations for non-required date fields
    for (const key in config) {
      const conf = config[key];
      if (conf.type === "date" && typeof conf.searchKey === "object" && cleanedValues[key]) {
        const dateValue = cleanedValues[key];
        if (dateValue.start && dateValue.end && dateValue.start.isAfter(dateValue.end)) {
          setErrorMessage(`${conf.label}: ${translate("Start date cannot be greater than end date")}`);
          return;
        }
        // Check if only one date is selected when both are required
        if (conf.required && ((dateValue.start && !dateValue.end) || (!dateValue.start && dateValue.end))) {
          setErrorMessage(`${conf.label}: ${translate("Both dates required")}`);
          return;
        }
      }
    }

    // Map values to search keys
    const mappedValues: { [key: string]: any } = {};
    Object.entries(config).forEach(([section, conf]) => {
      if (cleanedValues[section] !== undefined) {
        if (conf.type === "date") {
          if (typeof conf.searchKey === "object") {
            if (cleanedValues[section]?.start) mappedValues[conf.searchKey.start] = cleanedValues[section].start;
            if (cleanedValues[section]?.end) mappedValues[conf.searchKey.end] = cleanedValues[section].end;
          } else if (typeof conf.searchKey === "string") {
            // Only map the selected date (start or end)
            if (cleanedValues[section]?.date) mappedValues[conf.searchKey] = cleanedValues[section].date;
          }
        } else if (typeof conf.searchKey === "string") {
          // Apply value mapping if available
          if (conf.valueMapping && Array.isArray(cleanedValues[section])) {
            // For checkbox arrays, map each value
            mappedValues[conf.searchKey] = cleanedValues[section].map((val: string) => 
              conf.valueMapping![val] || val
            );
          } else if (conf.valueMapping && typeof cleanedValues[section] === 'string') {
            // For radio buttons, map the single value
            mappedValues[conf.searchKey] = conf.valueMapping[cleanedValues[section]] || cleanedValues[section];
          } else {
            mappedValues[conf.searchKey] = cleanedValues[section];
          }
        }
      }
    });
    setErrorMessage('');
    setValues(cleanedValues);
    if (Object.keys(mappedValues).length === 0) {
      setActiveSection(Object.keys(config)[0]);
    }
    onApply(mappedValues);
    setIsOpen(false);
  };
  const handleClear = () => {
    setValues({});
    setErrorMessage('');
    onClear && onClear();
    setIsOpen(false);
    setActiveSection(Object.keys(config)[0])
  };

  // Check if any filters are applied
  const hasActiveFilters = () => {
    return Object.entries(config).some(([key, conf]) => {
      const value = values[key];
      if (value === null || value === undefined) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (conf.type === 'date') {
        if (typeof conf.searchKey === 'string') {
          // Single date filter
          return !!value?.date;
        } else if (typeof conf.searchKey === 'object') {
          // Date range filter
          return value.start || value.end;
        }
      }
      if (typeof value === 'object' && value !== null) {
        // For other objects
        return Object.values(value).some(v => v);
      }
      return value !== '';
    });
  };

  // UI
  return (
    <div className={`flex justify-end relative ${className}`} ref={panelRef}>
      <ButtonWithImg
          buttonName={translate("Filter")}
          ImgSrc={loading ? 'loading-filter' : 'filter'}
          className="px-4"
          buttonOnclick={() => setIsOpen((prev) => !prev)}
          disabledButton={!!loading || !!disabled}
          testId={'filter'}
        />
      {isOpen && (
        <div className="py-[26px]  px-4 bg-white border border-s_dark_hover shadow-lg rounded-lg absolute right-0 top-10 mt-2 z-20 min-w-[675px]">
          <div className="w-full ">
            <p className="text-heading4 text-primary_text mb-4">{translate("Filter By")}</p>
            <hr className="2xl:mb-4 mb-2 border-neutral-300" />
            <div className={`flex ${customHeight ||'h-dynamicFilterHeight'}`}>
              {/* Section headings */}
              <div
                className={
                  `${notEnglish ? 'border-l ml-4' : 'border-r'} min-w-[141px] max-h-[270px] overflow-y-auto scrollBar ` +
                  (Object.keys(config).length === 1 ? ' flex items-center' : '')
                }
              >
                {Object.entries(config).map(([key, conf]) => (
                  <p
                    key={key}
                    className={`pb-4 pr-3 cursor-pointer  ${activeSection === key ? "text-primary_text" : "text-secondary_text"} ` +
                      (Object.keys(config).length === 1 ? 'w-full font-medium text-[20px]' : 'text-title1')}
                    onClick={() => setActiveSection(key)}
                    data-testid= {key}
                  >
                    {conf.label}
                  </p>
                ))}
              </div>
              {/* Section content */}
              <div className="pl-4 max-w-[394px] w-full overflow-y-auto scrollBar" style={{ minHeight: "100px" }}>
                {(() => {
                  const conf = config[activeSection];
                  if (!conf) return null;
                  if (conf.type === "checkbox") {
                    return (conf.options || []).map((option) => (
                      <CheckBoxWithLabel
                        key={option}
                        label={option}
                        handelCheckBox={() => handleCheckbox(activeSection, option)}
                        checked={Array.isArray(values[activeSection]) && values[activeSection].includes(option)}
                        id={option}
                      />
                    ));
                  }
                  if (conf.type === "radio") {
                    return (
                      <CustomRadioButton
                        options={(conf.options || []).map((opt) => ({ id: opt, label: opt }))}
                        selected={
                          Array.isArray(values[activeSection])
                            ? (values[activeSection][0] || conf.default || "")
                            : (typeof values[activeSection] === 'string' ? values[activeSection] : (conf.default || ""))
                        }
                        onChange={(val) => handleRadio(activeSection, val)}
                      />
                    );
                  }
                  if (conf.type === "date") {
                    // If searchKey is a string, show only one date picker
                    if (typeof conf.searchKey === "string") {
                      return (
                        <div className="flex justify-between gap-3">
                          <CustomDatePicker
                            testID="single-date"
                            label={translate("Select Date")}
                            handleStates={(date) => {
                              setValues((prev) => ({ ...prev, [activeSection]: { date } }));
                              if (date) setErrorMessage("");
                            }}
                            value={values[activeSection]?.date || null}
                            error={typeof errorMessage === 'string' ? errorMessage : ''}
                            disabled={false}
                            fieldError={!!errorMessage}
                            type="single"
                            required={!!conf.required}
                            classType={'filter'}
                          />
                        </div>
                      );
                    }
                    // If searchKey is an object, show two date pickers
                    if (typeof conf.searchKey === "object") {
                      return (
                        <div className="flex justify-between gap-3">
                          <CustomDatePicker
                            testID="start-date"
                            label={translate("Start Date")}
                            handleStates={(date) => handleDateChange(activeSection, date, "start")}
                            value={values[activeSection]?.start || null}
                            error={typeof errorMessage === 'object' && errorMessage?.start ? errorMessage.start : ''}
                            disabled={false}
                            fieldError={!!(typeof errorMessage === 'object' && errorMessage?.start)}
                            type="start"
                            required={true}
                            classType={'filter'}
                          />
                          <CustomDatePicker
                            testID="end-date"
                            label={translate("End Date")}
                            handleStates={(date) => handleDateChange(activeSection, date, "end")}
                            value={values[activeSection]?.end || null}
                            required={true}
                            error={typeof errorMessage === 'object' && errorMessage?.end ? errorMessage.end : ''}
                            disabled={false}
                            fieldError={!!(typeof errorMessage === 'object' && errorMessage?.end)}
                            type="end"
                            classType={'filter'}
                          />
                        </div>
                      );
                    }
                  }
                  return null;
                })()}
                {typeof errorMessage === 'string' && errorMessage &&
                  !(
                    config[activeSection]?.type === "date" &&
                    typeof config[activeSection]?.searchKey === "string"
                  ) && (
                    <div className="pt-1">
                      <ErrorMessage errorMessage={errorMessage} />
                    </div>
                  )}
              </div>
            </div>
            <div className="gap-3 flex pt-2">
              <Button
                buttonName={translate("Clear All")}
                textSize="text-large"
                buttonHight="h-10"
                testId="clear_all"
                textColor={hasActiveFilters() ? "text-p_primary" : ""}
                className={`px-4 ${hasActiveFilters() ? "bg-white border border-p_primary" : "border border-disable_stroke"}`}
                buttonOnclick={handleClear}
                buttonDisabled={!hasActiveFilters() || !!loading || !!disabled}
              />
              <Button
                buttonName={translate("Apply")}
                textSize="text-large"
                buttonHight="h-10"
                testId="apply_filter"
                className={`px-4 bg-p_primary text-white`}
                buttonOnclick={handleApply}
                buttonDisabled={!!loading || !!disabled}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicFilterPanel;